import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import StatusBadgeCst from "@components/StatusBadgeCst";
import { CustomerListItem } from "../types";

export const getDisplayName = (item: CustomerListItem): string => {
  const parts = [item.name, item.surname].filter(Boolean) as string[];
  return parts.length > 0 ? parts.join(" ") : item.username || "—";
};

export const getRowId = (item: CustomerListItem): number => {
  return Number(item.id_user ?? item.id ?? 0);
};

const formatRoles = (
  roles: CustomerListItem["roles"] | undefined,
): string | null => {
  if (!roles || roles.length === 0) return null;
  return roles
    .map((r) => (typeof r === "string" ? r : r?.label || r?.name || ""))
    .filter(Boolean)
    .join(", ");
};

const StakeholderListRow: React.FC<{ item: CustomerListItem }> = ({ item }) => {
  const theme = useTheme();
  const { t } = useTranslation("stakeholders-picker");

  const id = getRowId(item);
  const roles = formatRoles(item.roles);
  const isActive = (item.status || "").toUpperCase() === "ACTIVE";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        flex: 1,
        minWidth: 0,
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 700,
            color: theme.palette.text.primary,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {getDisplayName(item)}
        </Typography>
        <Typography
          sx={{
            fontSize: "12px",
            color: theme.palette.text.tertiary,
            lineHeight: 1.4,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {[id ? `ID: ${id}` : null, item.email, roles]
            .filter(Boolean)
            .join(" | ")}
        </Typography>
      </Box>

      {item.status && (
        <StatusBadgeCst
          label={isActive ? t("status.active") : t("status.inactive")}
          variant={isActive ? "active" : "default"}
        />
      )}
    </Box>
  );
};

export default StakeholderListRow;
